import {
  CardTable,
  EntityName,
  EntityList,
  Link,
  TableBottomCell
} from '@/components';
import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';
import {
  useTableProps,
  CryptoIcon,
  Flex,
  Box,
  CurrencyValue,
  PercentageValue,
  TableSkeleton
} from '@blockanalitica/ui';
import styled from 'styled-components';
import { useApi } from '@/api';

const Network = styled(Box)`
  text-transform: capitalize;
`;

type MarketData = {
  address: string;
  name: string;
  underlying_symbol: string;
  underlying_address: string;
  total_supply_usd: string | null;
  total_borrow_usd: string | null;
  total_collateral_usd: string | null;
  utilization: string | null;
  supply_rate: string | null;
  borrow_rate: string | null;
  network: string;
  collaterals: { symbol: string; address: string }[];
  total_supply_usd_change: string | null;
  total_collateral_usd_change: string | null;
  total_borrow_usd_change: string | null;
  utilization_change: string | null;
  supply_rate_change: string | null;
  borrow_rate_change: string | null;
};

export default function MarketsTable() {
  const { sorting } = useTableProps({
    initialSorting: '-total_supply_usd'
  });

  const [data, error] = useApi<MarketData[]>({
    url: '/all-markets/',
    queryParams: {
      sort: sorting.currentSorting
    }
  });

  if (error) {
    throw error;
  }

  if (!data) {
    return <TableSkeleton rows={10} />;
  }

  const columns: TableColumn<MarketData>[] = [
    {
      header: 'Market',
      getCell: (row) => (
        <>
          <EntityName
            entitySize="4"
            symbol1={<CryptoIcon name={`${row.network}_bw`} size="4" />}
            symbol2={
              <CryptoIcon
                name={row.underlying_symbol}
                address={row.underlying_address}
                network={row.network}
                size="4"
              />
            }>
            <Flex flexDirection="column" gap="4xs">
              <Box>{row.name}</Box>
              <Box color="muted" fontSize="-2">
                <Network display="inline-flex">{row.network}</Network> -{' '}
                {row.underlying_symbol}
              </Box>
            </Flex>
          </EntityName>
        </>
      ),
      cellAlign: 'flex-start'
    },
    {
      header: 'Total Earning',
      getCell: (row) => (
        <CurrencyValue
          value={row.total_supply_usd}
          options={{
            currency: 'USD'
          }}
        />
      ),
      getCellBottom: (row) => (
        <TableBottomCell checkValue={row.total_supply_usd_change}>
          <CurrencyValue
            value={row.total_supply_usd_change}
            options={{
              currency: 'USD',
              trend: true
            }}
          />
        </TableBottomCell>
      ),
      sortingField: 'total_supply_usd',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: 'Total Borrowing',
      getCell: (row) => (
        <CurrencyValue
          value={row.total_borrow_usd}
          options={{
            currency: 'USD'
          }}
        />
      ),
      getCellBottom: (row) => (
        <TableBottomCell checkValue={row.total_borrow_usd_change}>
          <CurrencyValue
            value={row.total_borrow_usd_change}
            options={{
              currency: 'USD',
              trend: true
            }}
          />
        </TableBottomCell>
      ),
      sortingField: 'total_borrow_usd',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: 'Utilization',
      getCell: (row) => <PercentageValue value={row.utilization} />,
      getCellBottom: (row) => (
        <TableBottomCell checkValue={row.utilization_change}>
          <PercentageValue
            value={row.utilization_change}
            options={{
              trend: true,
              iconSize: '-1'
            }}
          />
        </TableBottomCell>
      ),
      sortingField: 'utilization',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: 'Net Supply APR',
      getCell: (row) => <PercentageValue value={row.supply_rate} />,
      getCellBottom: (row) => (
        <TableBottomCell checkValue={row.supply_rate_change}>
          <PercentageValue
            value={row.supply_rate_change}
            options={{
              trend: true,
              iconSize: '-1'
            }}
          />
        </TableBottomCell>
      ),
      sortingField: 'supply_rate',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: 'Net Borrow APR',
      getCell: (row) => <PercentageValue value={row.borrow_rate} />,
      getCellBottom: (row) => (
        <TableBottomCell checkValue={row.borrow_rate_change}>
          <PercentageValue
            value={row.borrow_rate_change}
            options={{
              trend: true,
              iconSize: '-1'
            }}
          />
        </TableBottomCell>
      ),
      sortingField: 'borrow_rate',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: 'Total Collateral',
      getCell: (row) => (
        <CurrencyValue
          value={row.total_collateral_usd}
          options={{
            currency: 'USD'
          }}
        />
      ),
      getCellBottom: (row) => (
        <TableBottomCell checkValue={row.total_collateral_usd_change}>
          <CurrencyValue
            value={row.total_collateral_usd_change}
            options={{
              currency: 'USD',
              trend: true
            }}
          />
        </TableBottomCell>
      ),
      sortingField: 'total_collateral_usd',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: 'Collateral Assets',
      getCell: (row) => (
        <EntityList>
          {row.collaterals.map((collateral) => (
            <CryptoIcon
              key={collateral.address}
              name={collateral.symbol}
              address={collateral.address}
              network={row.network}
              size="1"
            />
          ))}
        </EntityList>
      ),
      cellAlign: 'flex-start',
      headerAlign: 'flex-start'
    }
  ];

  return (
    <CardTable<MarketData>
      title="Markets"
      rows={data}
      columns={columns}
      getKey={(row) => `${row.network}-${row.address}`}
      rowLinkProps={{
        to: (row) => `${row.network}/${row.address}`,
        // @ts-ignore
        linkComponent: Link
      }}
      sorting={sorting}
    />
  );
}
